import menuHeroDefaults from 'src/shared/menuPageHeroDefaults.json'
import { createProductPagePresentation } from 'src/pageBuilder/productPagePresentation'
import type {
  MenuPageBuilder,
  MenuPageBuilderBlock,
  MenuPageBuilderLinkItem,
  MenuPageBuilderProductsBlock,
} from 'src/shared/dbTypes/menuPageBuilder'
import navigationData from './navigationData.json'
import imageData from './editorialImages.json'
import homeCollections from './homeCollections.json'
import { brandRoutes, brandScenes, brandMilieus, brandSceneKey, brandSceneImageUrl, brandMenuBrands } from './brandScenes'

type Topic = 'home' | 'wohnen' | 'schlafen' | 'tisch' | 'kueche' | 'bad' | 'bademaentel' | 'dekoration' | 'lifestyle' | 'homewear' | 'duft' | 'waeschepflege' | 'geschenke' | 'glaeser'
type NavigationEntry = { menueId?: number; brand?: number; title: string; path?: string; href?: string; children?: NavigationEntry[] }
type PageInfo = { title: string; path: string; topic: string }
const menuPages: Record<string, PageInfo> = navigationData.menuPages
const navigation: NavigationEntry[] = navigationData.tree

const imageRegistry: Record<string, { filename: string; alt: string }> = imageData
const topicAssets: Record<Topic, string> = {
  home: 'home', wohnen: 'menu-33001335', schlafen: 'menu-33001336', tisch: 'menu-1325',
  kueche: 'menu-33001337', bad: 'menu-1326', bademaentel: 'menu-1017', dekoration: 'menu-1064',
  lifestyle: 'menu-33001338', homewear: 'menu-33001339', duft: 'menu-33001418',
  waeschepflege: 'menu-1026', geschenke: 'menu-33001419', glaeser: 'menu-1086',
}
const imagePath = (key: string) => `/api/images/landingpages/202/${imageRegistry[key].filename}`
export const editorialImage = (topic: Topic) => imagePath(topicAssets[topic])
const editorialAlt = (topic: Topic) => imageRegistry[topicAssets[topic]].alt
const pageImage = (topic: Topic, menuId?: number, brandId?: number) => {
  const key = [
    menuId && brandId ? `brand-menu-${brandId}-${menuId}` : undefined,
    menuId ? `menu-${menuId}` : undefined,
    !menuId && brandId ? `brand-${brandId}` : undefined,
    topicAssets[topic],
  ].find(key => key && imageRegistry[key])!
  return { src: imagePath(key), alt: imageRegistry[key].alt }
}

const worlds: { id: number; topic: Topic; title: string; body: string }[] = [
  { id: 33001335, topic: 'wohnen', title: 'Wohnen', body: 'Kleine Details. Ein schönes Zuhause.' },
  { id: 33001336, topic: 'schlafen', title: 'Schlafen', body: 'Weiche Textilien für deine Ruhezeit.' },
  { id: 1325, topic: 'tisch', title: 'Tisch', body: 'Für die Momente, die wir teilen.' },
  { id: 33001337, topic: 'kueche', title: 'Küche', body: 'Schöne Helfer für jeden Tag.' },
  { id: 1326, topic: 'bad', title: 'Bad', body: 'Mach aus Alltag eine kleine Auszeit.' },
  { id: 33001338, topic: 'lifestyle', title: 'Lifestyle', body: 'Lieblingsrituale für dich und dein Zuhause.' },
  { id: 33001339, topic: 'homewear', title: 'Homewear & Freizeit', body: 'Lieblingsstücke, die dich begleiten.' },
]

const discoveryMenuIds = [...worlds.map(world => world.id), 1001, 1061, 1064, 1017, 1026, 33001418, 33001419, 33001423]

const topicBodies: Record<Topic, string> = {
  home: 'Schöne Dinge für deinen Alltag. Entdecke unsere Wohnwelten und finde deine neuen Lieblingsstücke.',
  wohnen: 'Textilien, Accessoires und kleine Details, mit denen dein Zuhause noch mehr nach dir aussieht.',
  schlafen: 'Entdecke Betttextilien und Lieblingsstücke für entspannte Nächte und gemütliche Morgen.',
  tisch: 'Für den ersten Kaffee, gemeinsame Mahlzeiten und liebevoll gedeckte Tische.',
  kueche: 'Praktische Helfer und schöne Details, die den Küchenalltag begleiten.',
  bad: 'Weiche Textilien und schöne Badaccessoires für deine tägliche Auszeit.',
  bademaentel: 'Entdecke weiche Bademäntel für gemütliche Morgen und entspannte Auszeiten.',
  dekoration: 'Vasen, Licht und liebevolle Details, die deinem Zuhause Persönlichkeit geben.',
  lifestyle: 'Entdecke schöne Begleiter für kleine Rituale und persönliche Wohlfühlmomente.',
  homewear: 'Schöne und praktische Begleiter für zu Hause und unterwegs.',
  duft: 'Entdecke Düfte und kleine Rituale für eine wohnliche Atmosphäre.',
  waeschepflege: 'Entdecke unsere Auswahl für die Pflege deiner Lieblingstextilien.',
  geschenke: 'Kleine Aufmerksamkeiten und schöne Ideen zum Verschenken.',
  glaeser: 'Für Wasser, Lieblingsdrinks und gemeinsam verbrachte Abende.',
}

const categoryBodies: Record<string, string> = {
  '1061': 'Weiche Wohndecken und leichte Foulards für gemütliche Stunden auf Sofa und Sessel.',
  '1072': 'Dekorative Tabletts und Schalen für Lieblingsstücke und kleine Arrangements.',
  '1020': 'Zahnputzbecher für einen aufgeräumten Waschplatz und schöne Details im Bad.',
  '1021': 'Toilettenbürsten, Papierhalter und praktische Accessoires für dein Badezimmer.',
}
const pageBody = (title: string, topic: Topic, menuId?: number, brandId?: number) => {
  if (menuId) return categoryBodies[String(menuId)]
    || (worlds.some(world => world.id === menuId) ? topicBodies[topic]
      : `${title} für deinen Alltag – entdecke unsere Auswahl und finde deine Lieblingsstücke.`)
  return brandId ? `Entdecke die Auswahl von ${title} und finde schöne Begleiter für deinen Alltag.` : topicBodies[topic]
}

// Every live menu ID has an audited semantic icon. Exact IDs/titles take
// precedence over fallback words: "Tablett" is not a tablet computer.
export const menuPictograms: Record<string, string> = Object.fromEntries(
  Object.entries({
  "sofa": [33001335],
  "blanket-folded": [1061, 1005, 1004, 33001378, 33001379, 33001421],
  "pillow": [1007, 1092, 1006, 33001373, 33001380],
  "lantern": [1067],
  "candlestick": [1078],
  "vase": [1064],
  "tray": [1072],
  "basket": [1080, 1127, 1025, 33001353],
  "planter": [1081],
  "ornament": [1158],
  "table": [1069, 1325, 33001420, 33001422],
  "stool": [1062],
  "mirror": [1084, 1022],
  "bed": [33001336, 1001, 1003, 33001348, 33001372, 33001374, 33001375],
  "cutlery": [1087],
  "plate": [1038, 1142, 33001363, 33001370],
  "glass": [1086, 33001367, 33001423],
  "cup": [1036, 33001362, 33001369],
  "jug": [1041, 33001368],
  "teapot": [1138, 33001364],
  "sugar-milk": [1145, 1150],
  "butter-dish": [1091],
  "egg-cup": [1042, 33001366],
  "bowl": [1040, 33001365, 33001371],
  "etagere": [1054],
  "textile": [1046, 1055, 1045, 1056, 1050, 1133],
  "kitchen": [33001337, 1047, 1090],
  "jar": [1049],
  "coffee-press": [1143],
  "thermos": [1141],
  "trash": [1034, 1103],
  "laundry": [1053, 1035, 1026, 1031, 33001425],
  "apron": [1051],
  "bath": [1326],
  "towels": [1014, 1018, 1100, 33001344, 33001347, 33001333, 33001341, 33001342, 33001349, 33001354, 33001358, 33001377],
  "robe": [1017, 33001345, 33001334, 33001351, 33001355, 33001359],
  "rug": [1015, 33001346, 33001340, 33001350, 33001356, 33001360, 33001376, 1060],
  "soap-dish": [1019, 1027],
  "toothbrush": [1020],
  "toilet-brush": [1021],
  "tissue-box": [1024],
  "towel-rail": [1032],
  "cosmetic-bag": [1101, 1023],
  "bath-cabinet": [1112],
  "soap": [33001338, 1114, 1122, 33001424],
  "gift": [1118, 1119, 1121, 33001419],
  "candle": [1063],
  "diffuser": [33001381, 33001418],
  "spray": [1117],
  "skin-lotion": [1113, 33001382],
  "deodorant": [1115],
  "bag": [33001339, 1110],
  "wallet": [1111],
  "shopping-bag": [1094],
  "laptop": [1102],
  "sleep-mask": [1098],
  "garden": [1129],
  "bath-accessories": [33001343, 33001352, 33001357],
  "sauna": [33001361],
  "pet-care": [1116],
  "bar-cart": [1070],
  "ice-bucket": [1088, 1151],
  "desk-lamp": [1137],
  "carafe": [1058],
  "lamp": [1136],
  "sparkle": [33001417],
  }).flatMap(([icon, ids]) => ids.map(id => [String(id), icon]))
)
const pictogramsByTitle: Record<string, string> = Object.fromEntries(
  Object.entries(menuPages).map(([id, page]) => [page.title.trim().toLocaleLowerCase('de'), menuPictograms[id]])
)

export const pictogramForTitle = (title: string, topic: Topic = 'wohnen', menuId?: number): string => {
  const exactIcon = (menuId ? menuPictograms[String(menuId)] : undefined)
    || pictogramsByTitle[title.trim().toLocaleLowerCase('de')]
  if (exactIcon) return `/images/template/pictograms/${exactIcon}.svg`

  const rules: [RegExp, string][] = [
    [/taschen\b|shopper|homewear|freizeit/i, 'bag'], [/laptop|tablethüllen/i, 'laptop'], [/geldbörse/i, 'wallet'],
    [/geschenk/i, 'gift'], [/wäschepflege|wäscheduft|cleaning|spülmittel/i, 'laundry'], [/duft|kerzen|windlicht/i, 'candle'],
    [/bademantel|bademäntel|schürze/i, 'robe'], [/handt|sauna|strandt/i, 'towels'], [/badematt|badteppich|teppich/i, 'rug'],
    [/bett|schlafen|faserdecken|daunendecken|schonbezug/i, 'bed'], [/kissen|schlafbrillen/i, 'pillow'],
    [/tischdeck|tischläuf|tischset|serviett|geschirrtücher/i, 'textile'], [/decke|foulard|textilien/i, 'blanket-folded'],
    [/schneid|küchenhelfer|küche/i, 'kitchen'], [/korb|körb|aufbewahrung|brot/i, 'basket'],
    [/vase|pflanz|garten/i, 'vase'], [/spiegel/i, 'mirror'], [/hocker|pouf/i, 'stool'], [/tisch|barwagen/i, 'table'],
    [/besteck|löffel/i, 'cutlery'], [/teller|servier|melamin/i, 'plate'], [/glas|gläser|karaff|eiskübel/i, 'glass'],
    [/tassen|becher|tasse/i, 'cup'], [/kannen|kaffeebereiter|krüge/i, 'teapot'], [/seif/i, 'soap'], [/schal|schüssel|butter|eier/i, 'bowl'],
    [/vorrat|flaschen|zucker|milch/i, 'jar'], [/lampe|leucht/i, 'lamp'],
    [/seif|körper|deo|pflege|kosmetik|meraki|lifestyle/i, 'soap'], [/bad|zahn|toilett|taschentuch/i, 'bath'],
  ]
  const icon = rules.find(([pattern]) => pattern.test(title))?.[1]
    || ({ home: 'sofa', wohnen: 'sofa', schlafen: 'bed', tisch: 'table', kueche: 'kitchen', bad: 'bath', bademaentel: 'robe', dekoration: 'vase', lifestyle: 'soap', homewear: 'bag', duft: 'candle', waeschepflege: 'laundry', geschenke: 'gift', glaeser: 'glass' } as const)[topic]
  return `/images/template/pictograms/${icon}.svg`
}

const menuHref = (id: number, brand?: NavigationEntry) => {
  const info = menuPages[String(id)]
  if (!info) return undefined
  const prefix = brand?.brand && brand.path ? `/b/${brand.path}/${brand.brand}` : ''
  return `${prefix}/m/${info.path}/${id}`
}

const findEntry = (entries: NavigationEntry[], key: 'menueId' | 'brand', id: number): NavigationEntry | undefined => {
  for (const entry of entries) {
    if (entry[key] === id) return entry
    const child = findEntry(entry.children || [], key, id)
    if (child) return child
  }
  return undefined
}

const collectMenuEntries = (entries: NavigationEntry[]): NavigationEntry[] => entries.flatMap(entry =>
  entry.menueId ? [entry] : collectMenuEntries(entry.children || []))

const themeForPage = (id: number, title: string): Topic => {
  if (/bademantel|bademäntel/i.test(title)) return 'bademaentel'
  if (/vase|pflanz|windlicht|dekoanhänger|dekotablett|kerzenleuchter/i.test(title)) return 'dekoration'
  const topic = menuPages[String(id)]?.topic
  if (topic && topic in topicBodies) return topic as Topic
  if (/cawö|vossen|möve|aquanova|rhomtuft|joop/i.test(title)) return 'bad'
  if (/formesse|elegante|paradies|alvi/i.test(title)) return 'schlafen'
  if (/greengate|bloomingville|rice|jacquard/i.test(title)) return 'tisch'
  if (/laundress|fragrances/i.test(title)) return 'waeschepflege'
  if (/meraki|porto|björk|bondi/i.test(title)) return 'lifestyle'
  if (/wouf/i.test(title)) return 'homewear'
  return 'wohnen'
}

const relatedCategories = (menuId: number | undefined, brandId: number | undefined, topic: Topic): MenuPageBuilderLinkItem[] => {
  const brand = brandId ? findEntry(navigation, 'brand', brandId) : undefined
  // A brand absent from the editorial navigation must never inherit unrelated
  // living-room categories. Keep all known brand links in that brand's scope.
  if (brandId && !brand) return []
  const entry = menuId ? findEntry(brand?.children || navigation, 'menueId', menuId) : brand
  let entries = collectMenuEntries(entry?.children || [])
  if (!entries.length && brand) entries = collectMenuEntries(brand.children || [])
  if (!entries.length && !brandId) {
    const relatedTopic: Topic = ({ bademaentel: 'bad', dekoration: 'wohnen', glaeser: 'tisch' } as Partial<Record<Topic, Topic>>)[topic] || topic
    const world = worlds.find(world => world.topic === relatedTopic) || worlds.find(world => world.topic === 'lifestyle')!
    entries = topic === 'home'
      ? navigation.filter(entry => Boolean(entry.menueId))
      : collectMenuEntries(findEntry(navigation, 'menueId', world.id)?.children || [])
  }
  const seen = new Set<number>()
  return entries.filter(item => item.menueId !== menuId && item.menueId
    && (!brandId || brandScenes[brandSceneKey(brandId, item.menueId)] !== null)
    && !seen.has(item.menueId) && Boolean(seen.add(item.menueId)))
    .map(item => ({
      label: item.title,
      href: menuHref(item.menueId!, brand)!,
      imageSrc: pictogramForTitle(item.title, themeForPage(item.menueId!, item.title), item.menueId),
      imageAlt: '',
    })).filter(item => item.href)
}

const chrome = (base?: MenuPageBuilder): MenuPageBuilder => ({
  ...base,
  enabled: true,
  layout: {
    ...base?.layout,
    stylePreset: 'individual', navigationPosition: 'top', navigationDisplay: 'inline',
    heroMobileMode: 'content', contentWidth: 'full', hideLegacyHeader: true,
  },
  theme: {
    ...base?.theme,
    backgroundColor: '#fffdf9', surfaceColor: '#f3ede5', textColor: '#342d28', mutedTextColor: '#74695e',
    accentColor: '#ba0063', accentContrastColor: '#ffffff',
    buttonBackgroundColor: '#ba0063', buttonHoverColor: '#970050', buttonTextColor: '#ffffff',
    purchaseButtonBackgroundColor: '#ba0063', purchaseButtonHoverColor: '#970050', purchaseButtonTextColor: '#ffffff',
    navigationBackgroundColor: '#fffdf9', navigationTextColor: '#342d28',
  },
  search: {
    showCategories: true, showBrands: true, showProducts: true,
    items: [
      ...[1001, 1061, 1325, 1326, 1064, 33001419, 33001417].map(id => ({
        label: menuPages[String(id)].title, href: menuHref(id)!,
        imageSrc: pageImage('home', id).src, imageAlt: pageImage('home', id).alt,
      })),
      { label: 'Unsere Marken', href: '/brands', subtitle: 'Lieblingsmarken entdecken', imageSrc: editorialImage('home'), imageAlt: editorialAlt('home') },
    ],
  },
  products: { ...base?.products, columnsPerRow: 4, columnsPerRowMobile: 2 },
  ...(base?.footer ? { footer: {
    ...base.footer, backgroundColor: '#f3ede5', surfaceColor: '#fffdf9', textColor: '#342d28',
    accentColor: '#ba0063', brandSectionBackgroundColor: '#f3ede5', legalBackgroundColor: '#ebe2d7',
  } } : {}),
  blocks: base?.blocks || [],
})

const serviceBlock: MenuPageBuilderBlock = {
  type: 'benefits', id: 'lz-service', paddingTop: 32, paddingBottom: 32, backgroundColor: '#f3ede5',
  variant: 'trustBar', columns: 4,
  items: [
    { title: 'Sicher bezahlen', body: 'PayPal, Kreditkarte und weitere Zahlungsarten.', icon: 'payment' },
    { title: 'Sorgsam verpackt', body: 'Deine Lieblingsstücke auf dem Weg zu dir.', icon: 'truck' },
    { title: 'Persönlich für dich da', body: 'Wir helfen bei Fragen zu deiner Bestellung.', icon: 'mail' },
    { title: '14 Tage Widerrufsrecht', body: 'In Ruhe zu Hause entscheiden.', icon: 'return' },
  ],
}

const homeProductRow = (
  collection: keyof typeof homeCollections,
  options: Pick<MenuPageBuilderProductsBlock, 'title' | 'subtitle' | 'actionLabel' | 'actionHref'>
    & Partial<Pick<MenuPageBuilderProductsBlock, 'backgroundColor' | 'cardBackgroundColor'>>,
): MenuPageBuilderProductsBlock => ({
  type: 'products', id: `lz-${collection}`,
  productKeys: homeCollections[collection], selectionMode: 'manual', inStockOnly: true,
  layout: 'editorial', display: 'sliderLine',
  backgroundColor: '#fffdf9', cardBackgroundColor: '#f3ede5',
  paddingTop: 36, paddingBottom: 44, imageSizePx: 200,
  showAttributes: false, showHookText: false, showPrice: true, showSpecialPrice: true,
  showAvailability: true, showHoverEffect: true, showWishlist: true,
  ...options,
})

const homeBuilder = (base?: MenuPageBuilder): MenuPageBuilder => ({
  ...chrome(base), useAsHomePage: true,
  hero: {
    layout: 'background', eyebrow: 'LiebesZuhause · Home & Living', title: 'Hier bin ich gern zu Hause.',
    body: 'Lieblingsstücke für kleine Auszeiten, gemeinsame Momente und ein Zuhause voller Leben.',
    imageSrc: editorialImage('home'), imageAlt: editorialAlt('home'),
    mobileImageSrc: editorialImage('home'), mobileImageAlt: editorialAlt('home'),
    cta: { label: 'Wohnen entdecken', href: menuHref(33001335)! },
    secondaryCta: { label: 'Unsere Marken', href: '/brands' },
    align: 'left', height: 'medium', overlayColor: '#34281f', overlayOpacity: 32,
  },
  blocks: [
    {
      type: 'categoryList', id: 'wohnwelten', title: 'Was macht dein Zuhause schön?', subtitle: 'Entdecke unsere Wohnwelten',
      paddingTop: 42, paddingBottom: 40, backgroundColor: '#fffdf9',
      items: discoveryMenuIds.map(id => ({ label: menuPages[String(id)].title, href: menuHref(id)!, imageSrc: imagePath(`menu-${id}`), imageAlt: imageRegistry[`menu-${id}`].alt })),
      columns: 7, display: 'compactSlider', itemShape: 'rounded', itemSize: 'large', textSize: 'medium', itemGap: 'normal', itemShadow: 'none', stretchItems: false,
    },
    homeProductRow('wohndetails', {
      title: 'Schöne Details für dein Zuhause',
      subtitle: 'Vasen, Kerzenlicht und kleine Wohnaccessoires in ruhigen Naturtönen.',
      actionLabel: 'Wohnen entdecken', actionHref: menuHref(33001335),
    }),
    {
      type: 'banner', id: 'lz-tischmomente', variant: 'split', title: 'Die schönsten Momente teilen wir.',
      body: 'Ein gedeckter Tisch, ein guter Kaffee und Zeit füreinander. Entdecke Geschirr und Tischaccessoires für jeden Tag.',
      imageSrc: editorialImage('tisch'), imageAlt: editorialAlt('tisch'),
      cta: { label: 'Alles für deinen Tisch', href: menuHref(1325)! },
      align: 'left', minHeight: 'medium', paddingTop: 0, paddingBottom: 24, backgroundColor: '#f3ede5',
    },
    homeProductRow('tisch', {
      title: 'Tischmomente in Naturtönen',
      subtitle: 'Helles Steingut, klares Glas und Tischaccessoires in Creme, Braun und Schwarz.',
      actionLabel: 'Alles für deinen Tisch', actionHref: menuHref(1325),
    }),
    {
      type: 'categoryList', id: 'lz-lieblingsplatz', title: 'Mach es dir gemütlich',
      subtitle: 'Weiche Decken und schöne Kissen für deinen Lieblingsplatz.',
      paddingTop: 36, paddingBottom: 32, backgroundColor: '#fffdf9',
      items: [
        { label: 'Wohndecken zum Einkuscheln', href: menuHref(1061)!, imageSrc: pageImage('wohnen', 1061).src, imageAlt: pageImage('wohnen', 1061).alt },
        { label: 'Kissen für deinen Lieblingsplatz', href: menuHref(1007)!, imageSrc: pageImage('wohnen', 1007).src, imageAlt: pageImage('wohnen', 1007).alt },
      ],
      columns: 2, display: 'grid', itemShape: 'rounded', itemSize: 'large', textSize: 'medium', itemGap: 'normal', itemShadow: 'none',
    },
    homeProductRow('cozy', {
      title: 'Lieblingsplätze zum Einkuscheln',
      subtitle: 'Wohndecken und Kissen in ruhigen Naturtönen mit einzelnen Beere-Akzenten.',
      actionLabel: 'Wohntextilien entdecken', actionHref: menuHref(33001335),
    }),
    {
      type: 'banner', id: 'lz-auszeit', variant: 'split', title: 'Ein bisschen Ruhe. Ganz für dich.',
      body: 'Weiche Handtücher, schöne Badaccessoires und kleine Rituale für entspannte Wohlfühlmomente.',
      imageSrc: editorialImage('bad'), imageAlt: editorialAlt('bad'),
      cta: { label: 'Deine kleine Auszeit', href: menuHref(1326)! },
      align: 'left', minHeight: 'medium', paddingTop: 0, paddingBottom: 24, backgroundColor: '#f3ede5',
    },
    homeProductRow('bad', {
      title: 'Eine kleine Auszeit im Bad',
      subtitle: 'Weiche Handtücher, schöne Badaccessoires und kleine Wohlfühlrituale in Naturtönen.',
      actionLabel: 'Alles für dein Bad', actionHref: menuHref(1326),
      backgroundColor: '#f3ede5', cardBackgroundColor: '#fffdf9',
    }),
    {
      type: 'categoryList', id: 'lz-kleine-freuden', title: 'Kleine Dinge, große Freude', paddingTop: 40, paddingBottom: 44,
      items: [
        { label: 'Ein schöner Duft für zu Hause', href: menuHref(33001418)!, imageSrc: editorialImage('duft'), imageAlt: editorialAlt('duft') },
        { label: 'Mit Liebe ausgesucht', href: menuHref(33001419)!, imageSrc: editorialImage('geschenke'), imageAlt: editorialAlt('geschenke') },
        { label: 'Gläser für gemeinsame Momente', href: menuHref(33001423)!, imageSrc: editorialImage('glaeser'), imageAlt: editorialAlt('glaeser') },
      ],
      columns: 3, display: 'grid', itemShape: 'rounded', itemSize: 'large', textSize: 'medium', itemGap: 'normal', itemShadow: 'none',
    },
    homeProductRow('alessi', {
      title: 'Design für deinen Küchenalltag',
      subtitle: 'Durchdachte Küchenhelfer, Naturholz und schöne Details für jeden Tag.',
      actionLabel: 'Küche entdecken', actionHref: '/m/kueche/33001337',
    }),
    {
      type: 'banner', id: 'lz-kinderwelt', variant: 'split', title: 'Kleine Lieblingsplätze. Große Fantasie.',
      body: 'Platz zum Spielen, Entdecken und Zusammenwachsen. Liebevolle Kinderzimmerdetails von Bloomingville MINI und fröhliches Kindergeschirr von RICE begleiten die kleinen Momente.',
      // Unchanged landscape catalog scene: Bloomingville MINI Gentrim, product 24487, image 8.
      imageSrc: '/images/template/editorial/kinder-bloomingville-mini.webp',
      imageAlt: 'Kinderzimmer mit Bloomingville MINI Spielgeschirr, einem Holzregal in Hausform und geflochtenen Körben',
      cta: { label: 'Bloomingville entdecken', href: '/b/bloomingville/210' },
      secondaryCta: { label: 'RICE entdecken', href: '/b/rice_by_rice/192' },
      align: 'left', minHeight: 'medium', paddingTop: 0, paddingBottom: 24, backgroundColor: '#f3ede5',
    },
    homeProductRow('kinder', {
      title: 'Für kleine Lieblingsmenschen',
      subtitle: 'Spielideen, Kindergeschirr und schöne Details von RICE und Bloomingville MINI.',
      actionLabel: 'Geschenkideen entdecken', actionHref: menuHref(33001419),
      backgroundColor: '#f3ede5', cardBackgroundColor: '#fffdf9',
    }),
    serviceBlock,
  ],
})

const pageBuilder = (page: Record<string, any>, site: MenuPageBuilder, menuId?: number, brandId?: number, count?: number): MenuPageBuilder => {
  brandId = brandId || (menuId ? brandMenuBrands[menuId] : undefined)
  const title = String(page.titel || page.title || 'Unsere Auswahl').trim()
  const topic = themeForPage(menuId || 0, title)
  const categories = relatedCategories(menuId, brandId, topic)
  const scene = brandId && count !== 0 ? brandScenes[brandSceneKey(brandId, menuId)] : undefined
  const milieu = brandId ? brandMilieus[brandSceneKey(brandId, menuId)] : undefined
  const image = brandId
    ? (scene && milieu ? { src: brandSceneImageUrl(scene), alt: milieu.alt } : undefined)
    : pageImage(topic, menuId)
  const base = page.pageBuilder as MenuPageBuilder | undefined
  const blocks = (base?.blocks || []).filter(block => block.type !== 'categoryList' && block.type !== 'categorySlider' && block.type !== 'banner')
  if (!blocks.some(block => block.type === 'products')) blocks.unshift({ type: 'products', id: 'lz-products', productKeys: [], selectionMode: 'page', layout: 'grid', display: 'grid' })
  return {
    ...chrome({ ...site, ...base }), useAsHomePage: false,
    hero: !brandId && menuId && (menuHeroDefaults['202'] as Record<string, MenuPageBuilder['hero']>)[menuId]
      ? (base?.shopHeroes?.['202'] || { ...(menuHeroDefaults['202'] as Record<string, MenuPageBuilder['hero']>)[menuId], title })
      : {
      layout: 'split', eyebrow: brandId ? brandRoutes[String(brandId)]?.title || 'LiebesZuhause' : 'LiebesZuhause', title, body: pageBody(title, topic, menuId, brandId),
      ...(image?.src ? {
        imageSrc: image.src, imageAlt: image.alt,
        mobileImageSrc: image.src, mobileImageAlt: image.alt,
      } : {}),
      align: 'left', height: 'medium', overlayOpacity: 0,
    },
    blocks: [
      ...(categories.length ? [{
        type: 'categoryList', id: 'lz-kategorien', title: menuId ? 'Weiter entdecken' : 'Entdecke die Kollektion',
        items: categories, columns: Math.min(categories.length, 8), display: 'compactSlider', itemShape: 'rounded',
        itemSize: 'small', textSize: 'small', itemGap: 'normal', itemShadow: 'none',
        paddingTop: 24, paddingBottom: 24, backgroundColor: '#fffdf9',
      } as MenuPageBuilderBlock] : []),
      ...blocks,
    ],
  }
}

/** Only the LiebesZuhause template installs this presentation hook. API records,
 * filters, product selections, canonical paths and SEO text are kept intact. */
export const customizeProductPages = createProductPagePresentation({
  shop: 202,
  requireExistingBuilder: true,
  homeBuilder,
  pageBuilder,
  markImage: (src, images) => {
    if (src.startsWith('/api/images/landingpages/202/lz-neutral-20261018-')
      || src.startsWith('/image/landingpages/202/lz-brand-milieus-20261018-')) images[src] = true
  },
})
